import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer,
    ɵlooseIdentical,
    HostBinding,
    Optional,
    Self,
    ViewChild
} from '@angular/core';
import {FocusMonitor} from '@angular/cdk/a11y';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {Subject} from 'rxjs/Subject';
import * as MediumEditor from 'medium-editor';
import {FormGroup, FormBuilder, NgControl, FormControl} from '@angular/forms';
import { MatFormFieldControl } from '@angular/material';


const noop = () => {};
@Component({
    selector: 'richtext',
    template: '<textarea #textarea></textarea>',
    providers: [
        {
            provide: MatFormFieldControl, useExisting: RichtextComponent
        }
    ],
    host: {
        '[class.floating]': 'shouldLabelFloat',
        '[attr.aria-describedby]': 'describedBy'
    }
})
export class RichtextComponent implements OnInit, OnDestroy, MatFormFieldControl<string> {
    static nextId = 0;
    stateChanges = new Subject<void>();
    focused = false;
    errorState = false;
    controlType = 'medium-editor';
    private _value = '';

    describedBy = '';

    get empty() {
        return !this.value;
    }
    
    get shouldLabelFloat() { 
        return this.focused || !this.empty; 
    }

    @Input() placeholder;

    @Input() id;

    @Input()
    get required() { return this._required; }
    set required(req) {
        this._required = coerceBooleanProperty(req);
    }
    private _required = false;

    @Input()
    get disabled() { return this._disabled; }
    set disabled(dis) {
        this._disabled = coerceBooleanProperty(dis);
    }
    private _disabled = false;

    @Input()
    get value() {
        return this._value;
    }
    set value(value) {
        if( !this.focused && this.editor && this.editor.getContent() != value ) {
            // we need to update medium editor when we are not updating ourself
            value = this.getValue(value);
            this.editor.setContent(value);
        }
        this._value = value;
    }
    @Output() valueChange = new EventEmitter();

    writeValue(value: any) {
        this._value = value;
        this._onChangeCallback(this.value);
    }

    private _onChangeCallback: (_:any) => void = noop;
    registerOnChange(fn: any) {
        this._onChangeCallback = fn;
    }
    private _onTouchedCallback: () => void = noop;
    registerOnTouched(fn: any) {
        this._onTouchedCallback = fn;
    }

    private editor;

    @Output() delete = new EventEmitter();
    @Output() enter = new EventEmitter();

    options = {
        placeholder: {
            text: '',
            hideOnClick: true
        },
        paste: {
            cleanPastedHTML: true,
            cleanReplacements: [
                ['“', '"'],
                ['”', '"'],
                ['’', "'"],
                ['‘', "'"],
                [/\n|\r/, ' '],
                [/<(h[1-6]|div|p|section|table)[^>]*>/gi, ''],
                [/<\/(h[1-6]|div|p|section|table)>/gi, '']
            ],
            cleanAttrs: [
                'class', 
                'style',
                'size', 
                'align',
                'id'
            ],
            cleanTags: [
                'meta',
                'style',
                'script',
                'center',
                'basefont',
                'font',
                'span',
                'code',
                'pre',
                'br',
                'table',
                'thead',
                'th',
                'tbody',
                'tr',
                'td'
            ]
        },
        disableReturn: true,
        disableDoubleReturn: true,
        targetBlank: true,
        imageDragging: false,
        toolbar: {
            buttons: [
                {
                    name: 'bold',
                    contentDefault: '<i class="material-icons">format_bold</i>'
                },
                {
                    name: 'italic',
                    contentDefault: '<i class="material-icons">format_italic</i>'
                },
                {
                    name: 'underline',
                    contentDefault: '<i class="material-icons">format_underline</i>'
                },
                {
                    name: 'anchor',
                    contentDefault: '<i class="material-icons">link</i>'
                }
            ]
        }
    };

    @Input() enableReturn;
    @Input() dialog;

    constructor(private el: ElementRef, fb: FormBuilder, private fm: FocusMonitor, @Optional() @Self() public ngControl: NgControl) {
        fm.monitor(el.nativeElement, true).subscribe((origin) => {
            this.focused = !!origin;
        });
        if (this.ngControl != null) { 
            this.ngControl.valueAccessor = this; 
        }
    }

    @ViewChild('textarea') textarea;
    
    ngOnInit() {
        let id = this.id;
        if( id ) {
            this.el.nativeElement.id = id;
        }
        if( this.enableReturn === true ) {
            this.options.disableDoubleReturn = false;
            this.options.disableReturn = false;
            this.options.paste.cleanReplacements = [
                ['“', '"'],
                ['”', '"'],
                ['’', "'"],
                ['‘', "'"],
                [/\n|\r/, ' '],
                [/<(h[1-6]|div)[^>]*>/gi, '<p>'],
                [/<\/(h[1-6]|div)>/gi, '</p>']
            ];
        }
        if( this.placeholder ) {
            this.options.placeholder.text = this.placeholder;
        }
        let el = this.textarea.nativeElement;
        this.editor = new MediumEditor(el, this.options);
        this.value = this.getValue(this.value);
        this.editor.setContent(this.value);
        this.updateModel = this.updateModel.bind(this);
        this.editor.subscribe('editableInput', this.updateModel);   
        this.editor.subscribe('editableKeydownEnter', (event) => {
            this.enter.emit(event);
        });
        this.editor.subscribe('editableKeydownDelete', (event) => {
            this.delete.emit(event);
        });
        if( this.dialog === true ) {
            const id = this.editor.id;
            const toolbar = document.getElementById('medium-editor-toolbar-' + id);
            if( toolbar.id ) {
                toolbar.classList.add('fixed');
            }
        }
    }
    getValue(value) {
        if( Array.isArray(value) ) {
            value = value.reduce((out, item) => out += '<p>' + item.text + '</p>', '');
        }
        return value;
    }

    /**
     * Emit updated model
     */
    updateModel() {
        let value = this.editor.getContent();
        value = this.stripTags(value);
        this.value = value;
        this.valueChange.emit(this.value);
        this.stateChanges.next();
    }
    stripTags(content) {
        if( this.enableReturn !== true ) {
            content = content.replace(/<p[^>]*>|<\/p>/gi, '');
        }
        content = content.replace(/<br>/gi, '');
        content = content.replace(/&nbsp;/gi, ' ');

        // grammarly fixes - grammarly doesn't trigger paste
        content = content.replace(/(\s|&nbsp;)<\/([abi])>/gi, ' </$2>');
        content = content.replace(/<([abi])[^>]*>(\s|&nbsp;)/gi, '<$1> ');
        return content;
    }

    /**
     * Remove MediumEditor on destruction of directive
     */
    ngOnDestroy(): void {
        this.editor.destroy();
        this.stateChanges.complete();
    }

    setDescribedByIds(ids: string[]) {
        this.describedBy = ids.join(' ');
    }

    onContainerClick(event: MouseEvent) { }

}


// WEBPACK FOOTER //
// /opt/atlassian/pipelines/agent/build/src/swarm/src/app/fields/richtext.component.ts